import { useState, useCallback, useEffect } from 'react'
import * as adminApi from '../services/adminApi.js'
import type { AdminWorkspace, PaginatedResponse } from '../types/index.js'

export function useAdminWorkspaces(initialPage = 1, limit = 20) {
  const [data, setData] = useState<PaginatedResponse<AdminWorkspace> | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(initialPage)

  const fetch = useCallback(async (p: number) => {
    setLoading(true)
    setError(null)
    try {
      const result = await adminApi.listWorkspaces(p, limit)
      setData(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load workspaces')
    } finally {
      setLoading(false)
    }
  }, [limit])

  useEffect(() => {
    void fetch(page)
  }, [fetch, page])

  const updateWorkspace = useCallback(
    async (id: string, payload: Partial<{ isActive: boolean }>) => {
      const updated = await adminApi.updateWorkspace(id, payload)
      setData((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          data: prev.data.map((w) => (w.id === id ? updated : w)),
        }
      })
      return updated
    },
    [],
  )

  // Plan assignment has no response body (204) and the workspace list's
  // denormalized seats/credits are only known server-side after the
  // cascade — refetch the current page to pick up the new values.
  const assignPlan = useCallback(
    async (id: string, planId: string | null) => {
      await adminApi.assignWorkspacePlan(id, planId)
      await fetch(page)
    },
    [fetch, page],
  )

  return { data, loading, error, page, setPage, refetch: () => fetch(page), updateWorkspace, assignPlan }
}
